import { inject, Injectable } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import { IViewMetadata, IGridViewMetadata } from "@tylertech/tyler-api-types";
import { RepositoryFormData, IAugmentedWithParent, EntityWrapper, LazyLoadedProxyHandler } from "../interfaces";
import { CachedRepositoryService, NavigationService } from "@tylertech/core-module";
import { MetadataService } from "./metadata.service";
import { MetadataActionService } from "./metadataActionService";
import { MetadataViewService } from "./metadataViewService";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { ProviderStorageService } from "./table";
import { MultipleEntityEditService } from "./multipleEntityEditService";
import { ResetStateService } from "./reset.state.service";

@Injectable({ providedIn: "root" })
export class MetadataLoadViewService extends ResetStateService
{
    private readonly $metadataService = inject(MetadataService);
    private readonly $providerStorageService = inject(ProviderStorageService);
    private readonly $multipleEntityEditService = inject(MultipleEntityEditService);
    private $repositoryService = inject(CachedRepositoryService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private $metadataActionService = inject(MetadataActionService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);

    public recalculating?: {[type: string]: boolean} = {};

    constructor()
    {
        super(inject(NavigationService));
    }

    LoadView(model: any, view: IViewMetadata, modelCache: RepositoryFormData): Promise<any[]>
    {
        const self = this;
        view["$HasBeenLoaded"] = true;
        if (this.$metadataViewService.IsGrid(view))
        {
            if (view.EditForm && self.recalculating[view.EditForm.BoundType])
            {
                this.$multipleEntityEditService.setFilteredOrder(view, this.$providerStorageService.LocalEntitiesStore[view.GridProperties["$providerId"]]);

                return this.$repositoryService
                    .RecalculateGridView({
                        Item1: modelCache,
                        Item2: view["GridId"],
                    })
                    .then(
                        rdc =>
                        {
                            self.$metadataActionService.ProcessAfterSave(rdc.AfterSaveContext, rdc.Entity, rdc);
                            modelCache.Merge(rdc);
                            delete self.recalculating[view.EditForm.BoundType];
                            self.$metadataService.FireGridReload(rdc.Type, true);
                            return this.doLoadGrid(model, view, modelCache);
                        },
                        self.$metadataActionService.GetVerificationHandler(
                            modelCache,
                            () => self.LoadView(model, view, modelCache),
                            model
                        )
                    );
            }
            else
            {
                return this.doLoadGrid(model, view, modelCache);
            }
        }
        if (this.$metadataViewService.IsRepositoryTypeView(view))
        {
            const typeName = !view.ViewMetaData || !view.ViewMetaData.TypeName ? null : view.ViewMetaData.TypeName;
            if (model && model["$$TypeName"] === typeName) return Promise.resolve([model]);
            if ((modelCache && modelCache.Type !== typeName) || view.SubPropertyName || !model)
            {
                const filter = this.createFilterString(model, view, modelCache);
                const ignoreCache = model?.SerializedRowState === "Added";
                const isInCache = modelCache.IsInCache(self.$repositoryService, typeName, "Fetch", filter);

                if (view.SubPropertyName && !isInCache && model)
                {
                    const r: any[] = [];
                    if (view.SubPropertyClassMetadata)
                    {
                        const property = view.SubPropertyClassMetadata.Properties[0];
                        if (
                            Utilities.IsNullOrUndefinedOrEmptyObject(model[view.SubPropertyName]) &&
                            !!property.FromColumnId &&
                            !!property.ToColumnId
                        )
                        {
                            const filter = self.$metadataService.CreateLoadFilter(
                                model,
                                property.ToColumnId,
                                property.FromColumnId,
                                property.ConstantFilter
                            );
                            return modelCache
                                .GetOrFetch(self.$repositoryService, property.ClassMetadata.TypeName, filter)
                                .then(x =>
                                {
                                    if (view.CreateIfNotExists && (!x || x.length === 0))
                                    {
                                        const rejectionHandler = self.$metadataActionService.GetVerificationHandler(
                                            modelCache,
                                            () => self.LoadView(model, view, modelCache),
                                            model
                                        );
                                        const getNewModel = JSON.stringify({ ConfirmationQuestions: modelCache.ConfirmationQuestions });
                                        return self.$repositoryService
                                            .GetNew(
                                                {
                                                    Item1: property.ClassMetadata.TypeName,
                                                    Item2: getNewModel,
                                                },
                                                filter
                                            )
                                            .then(newX =>
                                            {
                                                if (!model[view.SubPropertyName])
                                                {
                                                    const wrapper = new EntityWrapper(newX, self.$metadataService);
                                                    model.SetLazyLoadParent(
                                                        property,
                                                        new Proxy(wrapper, new LazyLoadedProxyHandler(self.$metadataService))
                                                    );
                                                }
                                                const entity = getEntityFromSubProperty(modelCache.Entity);
                                                if (entity)
                                                {
                                                    r.push(entity);
                                                    return Promise.resolve(r);
                                                }
                                            }, rejectionHandler);
                                    }
                                    if (Array.isArray(x) && !model[view.SubPropertyName])
                                    {
                                        const wrapper = new EntityWrapper(x[0], self.$metadataService);
                                        model.SetLazyLoadParent(
                                            property,
                                            new Proxy(wrapper, new LazyLoadedProxyHandler(self.$metadataService))
                                        );
                                    }
                                    const entity = getEntityFromSubProperty(modelCache.Entity);
                                    if (entity)
                                    {
                                        r.push(entity);
                                        return Promise.resolve(r);
                                    }
                                });
                        }
                        r.push(model[view.SubPropertyName]);
                        return Promise.resolve(r);
                    }
                    else if (!view.SubPropertyType || view.SubPropertyType === modelCache.Type)
                    {
                        const entity = getEntityFromSubProperty(modelCache.Entity);
                        if (entity)
                        {
                            r.push(entity);
                            return Promise.resolve(r);
                        }
                    }
                    else if (view.SubPropertyType && modelCache.OtherEntities[view.SubPropertyType])
                    {
                        const entity = getEntityFromSubProperty(modelCache.OtherEntities[view.SubPropertyType][0]);
                        if (entity)
                        {
                            r.push(entity);
                            return Promise.resolve(r);
                        }
                    }
                }
                return modelCache.GetOrFetch(self.$repositoryService, typeName, filter, ignoreCache).then(r =>
                {
                    if (view.CreateIfNotExists && (!r || r.length === 0))
                    {
                        const rejectionHandler = self.$metadataActionService.GetVerificationHandler(
                            modelCache,
                            () => self.LoadView(model, view, modelCache),
                            model
                        );
                        const getNewModel = JSON.stringify({ ConfirmationQuestions: modelCache.ConfirmationQuestions });
                        return self.$repositoryService
                            .GetNew(
                                {
                                    Item1: typeName,
                                    Item2: getNewModel,
                                },
                                filter
                            )
                            .then(x =>
                            {
                                x["$$Parent"] = modelCache.Entity;
                                modelCache.Add(typeName, x, filter);
                                return [x];
                            }, rejectionHandler);
                    }
                    if (modelCache && r)
                    {
                        if (!model)
                        {
                            model = Array.isArray(r) ? r[0] : r;
                            modelCache.Entity = model;
                        }
                        else if (Array.isArray(r))
                        {
                            if (view.SubPropertyName)
                            {
                                if (view.SubPropertyType && modelCache.OtherEntities[view.SubPropertyType])
                                {
                                    if (modelCache.OtherEntities[view.SubPropertyType][0][view.SubPropertyName])
                                        r[0] = modelCache.OtherEntities[view.SubPropertyType][0][view.SubPropertyName];
                                    else modelCache.OtherEntities[view.SubPropertyType][0][view.SubPropertyName] = r[0];
                                }
                                if (!view.SubPropertyType || view.SubPropertyType === modelCache.Type)
                                {
                                    if (modelCache.Entity[view.SubPropertyName])
                                        r[0] = modelCache.Entity[view.SubPropertyName];
                                    else modelCache.Entity[view.SubPropertyName] = r[0];
                                }
                            }
                            else
                            {
                                for (let i = 0; i < r.length; i++)
                                {
                                    r[i]["$$Parent"] = modelCache.Entity;
                                    modelCache.Add(typeName, r[i], filter);
                                }
                            }
                        }
                        else
                        {
                            //Shouldn't set the parent because you can't be your own parent
                            modelCache.Add(typeName, r, filter);
                        }
                    }
                    return r;
                });
            }
        }
        if (view.SubPropertyName && !Array.isArray(model) && !this.$metadataViewService.IsCombinedView(view))
        {
            return Promise.resolve([getEntityFromSubProperty(model)]);
        }
        function getEntityFromSubProperty(entity: any): any[]
        {
            const chain = view.SubPropertyName.split(".");
            let subPropertyEntity = Object.assign({}, entity);
            let parent = entity;
            for (let i = 0; i < chain.length; i++)
            {
                const element = chain[i];
                subPropertyEntity = subPropertyEntity[element];
                if (subPropertyEntity) subPropertyEntity["$$Parent"] = parent;
                parent = subPropertyEntity;
            }
            return subPropertyEntity;
        }
        return Promise.resolve(null);
    }

    doLoadGrid(model: any, view: IGridViewMetadata, modelCache: RepositoryFormData): Promise<any[]>
    {
        const self = this;
        const viewClassType = view.GridProperties.ClassMetadata.TypeName;
        if (view.SubPropertyName)
        {
            if (view.SubPropertyType && modelCache)
            {
                const filter = this.createFilterString(model, view, modelCache);
                return modelCache.GetOrFetch(self.$repositoryService, view.SubPropertyType, filter).then(x =>
                {
                    model = x[0];
                    model.$$TypeName = view.SubPropertyType;
                    modelCache.Add(view.SubPropertyType, model, filter);
                    // model = modelCache.OtherEntities[view.SubPropertyType][0];
                    if (!model)
                    {
                        throw new Error(
                            `${MetadataLoadViewService.name}.LoadView|model was null but ViewMetadata specified a SubProperty.`
                        );
                    }
                    return getEntitiesFromSubProperty();
                });
            }
            return getEntitiesFromSubProperty();
        }
        let filter = this.createFilterString(model, view, modelCache);
        if (Utilities.IsNullOrUndefined(filter))
        {
            filter = null;
        }
        if (view.ParentColumnName && !Utilities.IsNullOrUndefined(model) && model.IsAdded)
        {
            const parentColumnValue = this.$metadataService.DependentModuleValue(view.ParentColumnName, null, model);
            if (Utilities.IsNullOrUndefined(parentColumnValue))
            {
                let children = modelCache.GetWithoutFetch(viewClassType, filter);
                if (!children)
                {
                    children = [];
                    modelCache.AddAll(viewClassType, children);
                }
                return Promise.resolve(children);
            }
        }
        return modelCache.GetOrFetch(self.$repositoryService, viewClassType, filter).then(children =>
        {
            if (children)
            {
                assignParent(children, model);
                const canAdd = view.GridProperties.CanAdd || view.CanAddProperty;
                const canEdit =
                    view.GridProperties.CanEdit || view.GridProperties.CanEditProperty || view.CanEditProperty;
                const canDelete =
                    view.GridProperties.CanDelete || view.GridProperties.CanDeleteProperty || view.CanDeleteProperty;
                const canCopy = view.GridProperties.CanCopy || view.GridProperties.CanCopyProperty;
                const hasActions = !!view.Actions && view.Actions.length > 0;
                const selectProperty = view.GridProperties.ClassMetadata.Properties.filter(p => p.Name === (view.SelectProperty ? view.SelectProperty : "Selected"))[0];
                const isSelectRecalculable = selectProperty && selectProperty.RecalculateOnChange;
                if (modelCache && (canAdd || canEdit || canDelete || canCopy || hasActions || isSelectRecalculable))
                {
                    modelCache.AddAll(viewClassType, children, filter);
                }
            }
            return children;
        });
        function getEntitiesFromSubProperty(fetchSubProperty: boolean = true): Promise<any[]>
        {
            const chain = view.SubPropertyName.split(".");
            const subProp = chain[chain.length - 1];
            if (view.SubPropertyClassMetadata && fetchSubProperty)
            {
                const subPropertyToFetch = chain[0];
                const property = view.SubPropertyClassMetadata.Properties[0];
                if (Utilities.IsNullOrUndefinedOrEmptyObject(model[subPropertyToFetch]))
                {
                    const filter = self.$metadataService.CreateLoadFilter(
                        model,
                        property.ToColumnId,
                        property.FromColumnId,
                        property.ConstantFilter
                    );
                    return modelCache
                        .GetOrFetch(self.$repositoryService, property.ClassMetadata.TypeName, filter)
                        .then(x =>
                        {
                            if (Array.isArray(x) && !model[subPropertyToFetch])
                            {
                                if (chain.length > 1)
                                {
                                    //sub property is an entity that has a grid on it somewhere in the chain
                                    const wrapper = new EntityWrapper(x[0], self.$metadataService);
                                    model.SetLazyLoadParent(property, new Proxy(wrapper, new LazyLoadedProxyHandler(self.$metadataService)));
                                }
                                else
                                {
                                    //sub property is a grid itself
                                    model[subPropertyToFetch] = x;
                                }
                            }
                            return getEntitiesFromSubProperty(false);
                        });
                }
                return getEntitiesFromSubProperty(false);
            }
            else
            {
                for (let i = 0; i < chain.length - 1; i++)
                {
                    const element = chain[i];
                    if (element === "$$Parent" && model["$$Parent"])
                    {
                        model = model["$$Parent"];
                        continue;
                    }
                    if (model[element]) model[element]["$$Parent"] = model;
                    model = model[element];
                }
            }
            if (Utilities.IsNullOrUndefined(model[subProp]))
            {
                model[subProp] = [];
            }
            let children: any[];
            if (Array.isArray(model[subProp]))
            {
                children = model[subProp];
            }
            else
            {
                children = [model[subProp]];
            }
            assignParent(children, model);
            return Promise.resolve(children);
        }
        function assignParent(children: IAugmentedWithParent[], parent: any): void
        {
            if (!children || !parent)
            {
                return;
            }
            for (const child of children)
            {
                child.$$Parent = parent;
            }
        }
    }

    createFilterString(model: any, view: IViewMetadata, modelCache: RepositoryFormData): string
    {
        let filter = this.$metadataViewService.createFilterString(model, view);
        if (modelCache.$ParentFormData && modelCache.$ParentFormData["$CopySourceId"])
        {
            //If from a copy, view has a parent/child relationship and is not the same entity from the grid that was copied.
            //This will allow the repository to retrieve a copied entity of the correct type for this view.
            const id = modelCache.$ParentFormData["$CopySourceId"];

            const builder = this.$odataFilterFactory.createBuilder();
            builder.filterEQ("CopyFromId", id);

            filter = filter + builder.build().replace("&$filter=", " and ");
        }

        return filter;
    }
}
